import axios from 'axios';

import { attachErrorLogging, attachOktaToken } from './oktaToken';

/**
 * Axios client for the Asset Library backend.
 *
 * Separate from `./client` because Asset Library is a separate Cloud Run
 * service, even though its UI ships inside the Content Tracker bundle. It is
 * reached on the Content Tracker origin all the same: the load balancer routes
 * `/asset-library/api/*` to that service and rewrites the prefix back to
 * `/api`, which is where the service mounts its routers.
 *
 * Same-origin is what keeps this simple. `withCredentials` stays false —
 * requests are authorised by bearer token only — and no build-time variable is
 * needed to name the backend per environment. The Vite dev server mirrors the
 * same rule onto the local backend on port 8001, so there is nothing to
 * configure locally either.
 *
 * That last point is load-bearing rather than tidy. Vite inlines
 * `import.meta.env` at build time, so when the deploy pipeline did not pass
 * the old `VITE_ASSET_LIBRARY_API_BASE_URL` its absolute default was baked into
 * the production bundle and every user's browser called its own machine on
 * port 8001. A hardcoded relative path cannot fail that way: it is correct in
 * any environment that serves the UI and the API from one origin, which is all
 * of them.
 *
 * The Okta token interceptor is shared with the Content Tracker client, so
 * both send the same user's token from one `setOktaAuth` call in the bootstrap.
 */

/**
 * The prefix every Asset Library call sits behind.
 *
 * Exported because not everything that talks to this backend can go through
 * axios: an OLA banner is rendered by pointing an iframe at
 * `${ASSET_LIBRARY_API_BASE}/ola/{token}/index.html`, and the browser then
 * resolves the banner's own relative references against it. Sharing the
 * constant is what stops that path drifting from the one the load balancer
 * routes.
 */
export const ASSET_LIBRARY_API_BASE = '/asset-library/api';

const assetLibraryClient = axios.create({
  // The `/asset-library` prefix is what the load balancer matches on and
  // removes; without it these calls hit the Content Tracker API instead.
  baseURL: ASSET_LIBRARY_API_BASE,
  // Sized for small JSON, which is almost everything here: uploads go straight
  // to GCS via signed URLs. The one exception is downloading a file
  // (`downloadAsset` in pages/asset-library/api.ts), which carries the bytes
  // and so overrides this per request with `responseType: 'blob'` and no
  // timeout.
  timeout: 60000,
  withCredentials: false,
});

attachOktaToken(assetLibraryClient);
attachErrorLogging(assetLibraryClient);

export default assetLibraryClient;
